import React from 'react';
import { graphql } from 'gatsby';
import { Link, useI18next } from 'gatsby-plugin-react-i18next';
import styled, { css, keyframes } from 'styled-components';

import Layout from '../../components/Layout';
import { SEO } from '../../components/SEO';
import { Box } from '../../components/Base';
import CTASection from '../../components/CallToActionBottomCard';
import {
  Heading,
  PrimaryLinkButton,
  Text,
  Title,
} from '../../components/CommonStyledComponents';
import { PARTNERSHIPS_LINK } from '../../components/Sections/Marketplace/Footer';
import { BASE_TITLES } from '../../constants/metadata';
import { META_TITLES, META_DESCRIPTIONS } from '../../data/metaDescriptions';
import getLanguageResource from '../../utils/getLanguageResource.js';
import { trackCustomerThroughPages } from '../../utils/analytics';
import media from '../../utils/media';
import { setupSmoothScrolling } from '../../utils/smooth-scroll';

import connectIcon from '../../assets/images/contact-us/connect.svg';
import contactUsHero from '../../assets/images/contact-us/contact-us-hero.webp';
import mapPin from '../../assets/images/contact-us/map-pin.svg';
import worldMap from '../../assets/images/contact-us/world-map.webp';
import partnerIcon from '../../assets/images/contact-us/partner.svg';
import supportIcon from '../../assets/images/contact-us/support.svg';
import unicornJoinTeam from '../../assets/images/contact-us/unicorn-contact-us.webp';

import facebook from '../../assets/icons/socials/facebook.svg';
import instagram from '../../assets/icons/socials/instagram.svg';
import linkedin from '../../assets/icons/socials/linkedin.svg';
import twitter from '../../assets/icons/socials/twitter-x.svg';
import youtube from '../../assets/icons/socials/youtube.svg';
import arrowRight from '../../assets/icons/arrow-right.svg';

import flagAu from '../../assets/images/contact-us/flags/au.svg';
import flagCa from '../../assets/images/contact-us/flags/ca.svg';
import flagEs from '../../assets/images/contact-us/flags/es.svg';
import flagFi from '../../assets/images/contact-us/flags/fi.svg';
import flagFr from '../../assets/images/contact-us/flags/fr.svg';
import flagGb from '../../assets/images/contact-us/flags/gb.svg';
import flagIt from '../../assets/images/contact-us/flags/it.svg';
import flagUs from '../../assets/images/contact-us/flags/us.svg';

const REGION_FLAG_SRC = {
  AU: flagAu,
  CA: flagCa,
  ES: flagEs,
  FI: flagFi,
  FR: flagFr,
  GB: flagGb,
  IT: flagIt,
  US: flagUs,
};

/** Pin tip as % of map frame — from Figma locators (10087:2291) vs map (10087:1706). */
const MAP_PIN_TIP_PCT = [
  { left: 27.5887, top: 36.3934 },
  { left: 15.2135, top: 34.7483 },
  { left: 54.4521, top: 29.2148 },
  { left: 53.6975, top: 36.6925 },
  { left: 48.4153, top: 36.8421 },
  { left: 49.019, top: 39.8332 },
  { left: 48.9436, top: 44.1703 },
  { left: 53.0183, top: 44.619 },
  { left: 34.9082, top: 80.6619 },
  { left: 59.6587, top: 68.847 },
  { left: 72.7885, top: 61.2197 },
  { left: 84.7865, top: 56.733 },
  { left: 91.9551, top: 88.4388 },
  { left: 18.911, top: 42.2261 },
  { left: 23.9667, top: 48.3579 },
  { left: 19.4392, top: 55.2375 },
];

/**
 * One full loop = ring expanding + time until repeat. Longer cycles leave a
 * clearer pause before the next pulse (less overlap with restart).
 */
const MAP_PIN_PING_DURATION_SEC = [
  3.95, 4.28, 4.62, 4.1, 4.88, 4.04, 4.55, 4.75, 4.32, 5.0, 4.18, 4.65, 4.48,
  4.22, 4.82, 4.38,
];

const MAP_PIN_PING_DELAY_SEC = [
  0, 0.62, 1.25, 0.35, 1.9, 0.78, 1.45, 2.1, 0.5, 1.15, 2.35, 0.95, 1.68,
  0.22, 1.32, 2.55,
];

export const query = graphql`
  query ContactUsPageQuery($language: String!) {
    locales: allLocale(filter: { language: { eq: $language } }) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
  }
`;

const PageRoot = styled.div`
  width: 100%;
`;

const HeroSection = styled.section`
  background: #7a3200;
  padding: 3.75rem 1.25rem 2.5rem;

  ${media.laptop`
    padding: 4.375rem 1.25rem 3.75rem;
  `}
`;

const HeroInner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2.5rem;
  max-width: 66.0625rem;
  margin: 0 auto;
  width: 100%;

  ${media.laptop`
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 3rem;
  `}
`;

const HeroCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2.5rem;
  max-width: 40.4375rem;
`;

const HeroVisual = styled.div`
  width: 100%;
  max-width: 24.75rem;
  margin: 0 auto;

  ${media.laptop`
    flex-shrink: 0;
    margin: 0;
  `}
`;

const HeroCompositeImg = styled.img`
  display: block;
  width: 100%;
  height: auto;
`;

const CardsSection = styled.section`
  padding: 2.5rem 1.25rem;
  background: #faf7f2;

  ${media.laptop`
    padding: 3.75rem 1.25rem 5rem;
  `}
`;

const CardsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.875rem;
  max-width: 83.4375rem;
  margin: 0 auto;

  ${media.laptop`
    flex-direction: row;
    align-items: stretch;
    gap: clamp(1rem, 2vw, 1.875rem);
  `}
`;

const ContactCard = styled.article`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 2.5rem 2.0625rem 2rem;
  border-radius: 1.25rem;
  background: #fff;
  box-shadow: 0 0.25rem 1.5rem rgba(45, 41, 38, 0.08);

  ${media.laptop`
    max-width: 26.5625rem;
    padding-bottom: 2.5rem;
  `}
`;

const cardBlockGapMobile = '2.109375rem';
const cardBlockGapDesktop = '2.8125rem';
const cardTitleBodyGapMobile = '1.5625rem';
const cardTitleBodyGapDesktop = '2.125rem';

const CardIcon = styled.img`
  width: 4.875rem;
  height: 4.875rem;
  display: block;
  margin-bottom: ${cardBlockGapMobile};

  ${media.laptop`
    margin-bottom: ${cardBlockGapDesktop};
  `}
`;

const CardActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${cardBlockGapMobile};
  margin-top: ${cardBlockGapMobile};

  ${media.laptop`
    gap: ${cardBlockGapDesktop};
    margin-top: ${cardBlockGapDesktop};
  `}
`;

const arrowLinkCss = css`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  font-family: ${(p) => p.theme.fonts.lato.bold};
  font-size: 0.8125rem;
  line-height: 150%;
  color: #2d2926;
  text-decoration: underline;
  text-underline-offset: 0.125rem;

  &:hover {
    color: #b94d00;
  }

  img {
    width: 1.125rem;
    height: auto;
    flex-shrink: 0;
  }

  ${media.laptop`
    font-size: 1.125rem;
  `}
`;

const CardPrimaryButton = styled(PrimaryLinkButton)`
  ${media.laptop`
    font-size: 1.125rem;
  `}
`;

const ArrowTextLink = styled.a`
  ${arrowLinkCss}
`;

const ArrowNavLink = styled(Link)`
  ${arrowLinkCss}
`;

const CardSubheading = styled(Heading).attrs({ as: 'h3' })`
  font-size: 1.25rem;
  margin: 0;
  color: #2d2926;
  line-height: 130%;

  ${media.laptop`
    font-size: 1.75rem;
  `}
`;

const CardBodyText = styled(Text)`
  margin: ${cardTitleBodyGapMobile} 0 0;
  font-size: 1rem;
  line-height: 160%;
  color: #2d2926;

  ${media.laptop`
    margin-top: ${cardTitleBodyGapDesktop};
    font-size: 1.125rem;
  `}
`;

const MapSection = styled.section`
  padding: 2.5rem 1.25rem 3rem;
  background: #fff;

  ${media.laptop`
    padding: 7.5rem 1.25rem 4.375rem;
  `}
`;

const MapInner = styled.div`
  max-width: 82.8125rem;
  margin: 0 auto;
  text-align: center;
`;

const mapPingExpand = keyframes`
  0% {
    transform: scale(0.42);
    opacity: 0.92;
  }

  100% {
    transform: scale(4.15);
    opacity: 0;
  }
`;

const mapPinScalePulse = keyframes`
  0%,
  100% {
    transform: scale(1);
  }

  50% {
    transform: scale(1.2);
  }
`;

const mapPinScalePulseDesktop = keyframes`
  0%,
  100% {
    transform: scale(1);
  }

  50% {
    transform: scale(1.8);
  }
`;

const MapFigure = styled.figure`
  position: relative;
  display: block;
  width: 100%;
  margin: 2.5rem 0 0;
  padding: 0;
  border: 0;
  line-height: 0;

  ${media.laptop`
    margin-top: 6.25rem;
  `}
`;

const MapPhoto = styled.img`
  display: block;
  width: 100%;
  height: auto;
  border-radius: 1.25rem;
`;

const MapPinsLayer = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
`;

const MapPinWrap = styled.div`
  position: absolute;
  left: ${(p) => p.$left}%;
  top: ${(p) => p.$top}%;
  transform: translate(-50%, -100%);
  z-index: 1;
`;

const MapPinImg = styled.img`
  display: block;
  width: 0.9375rem;
  height: 1.375rem;
  object-fit: contain;
  position: relative;
  z-index: 1;
  transform-origin: center bottom;
  animation: ${mapPinScalePulse} ${(p) => p.$duration}s ease-in-out infinite;
  animation-delay: ${(p) => p.$delay}s;

  ${media.laptop`
    animation: ${mapPinScalePulseDesktop} ${(p) => p.$duration}s ease-in-out
      infinite;
    animation-delay: ${(p) => p.$delay}s;
  `}

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const MapPinPing = styled.div`
  position: absolute;
  left: 50%;
  top: 100%;
  width: 0.5625rem;
  height: 0.5625rem;
  margin-left: -0.28125rem;
  margin-top: -0.28125rem;
  border: 1px solid rgba(253, 99, 0, 0.95);
  border-radius: 50%;
  box-shadow:
    0 0 0 1px rgba(255, 252, 248, 0.92),
    0 0 10px 3px rgba(253, 99, 0, 0.5);
  pointer-events: none;
  z-index: 0;
  transform-origin: center center;
  animation: ${mapPingExpand} ${(p) => p.$duration}s cubic-bezier(0.22, 0.61, 0.36, 1)
    infinite;
  animation-delay: ${(p) => p.$delay}s;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    opacity: 0;
  }
`;

const RegionalPhonesWrap = styled.div`
  margin-top: 3rem;
  padding-top: 3rem;
  width: 100%;
  text-align: center;
  scroll-margin-top: 8rem;

  ${media.tablet`
    text-align: left;
  `}
`;

const RegionalPhonesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 3.125rem;
  text-align: center;

  ${media.tablet`
    grid-template-columns: repeat(2, 1fr);
    column-gap: 3rem;
    row-gap: 4rem;
    text-align: left;
  `}

  ${media.laptop`
    grid-template-columns: repeat(3, 1fr);
    column-gap: 12.8125rem;
    row-gap: 6.25rem;
  `}
`;

const PhoneRegionBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.40625rem;

  ${media.tablet`
    align-items: flex-start;
    gap: 1.875rem;
  `}
`;

const RegionTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.625rem;

  ${media.tablet`
    justify-content: flex-start;
  `}
`;

const FlagImg = styled.img`
  width: 1.25rem;
  height: 0.9375rem;
  flex-shrink: 0;
  object-fit: cover;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 1px;
`;

const RegionTitle = styled(Heading).attrs({ as: 'h3' })`
  margin: 0;
  font-family: ${(p) => p.theme.fonts.montserrat.semibold};
  font-size: 1.5rem;
  color: #2d2926;
  line-height: 130%;
`;

const RegionLines = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.40625rem;
  width: 100%;
  text-align: center;

  ${media.tablet`
    text-align: left;
    gap: 1.875rem;
  `}
`;

const RegionLine = styled.div`
  font-family: ${(p) => p.theme.fonts.lato.base};
  font-size: 1.125rem;
  line-height: 150%;
  color: #2d2926;
`;

const RegionLineLabel = styled.span`
  margin-right: 0.25rem;
`;

const RegionValueLink = styled.a`
  font-family: ${(p) => p.theme.fonts.lato.base};
  font-size: 1.125rem;
  line-height: 150%;
  color: #2d2926;
  text-decoration: underline;
  text-underline-offset: 0.125rem;

  &:hover {
    color: #b94d00;
  }
`;

const CTASectionWrapper = styled.div`
  padding-top: 2.5rem;

  > div {
    padding-bottom: 2.5rem;
  }

  ${media.laptop`
    > div {
      padding-bottom: 7.5rem;
    }
  `}
`;

const JoinSection = styled.section`
  position: relative;
  left: 50%;
  right: 50%;
  width: 100vw;
  margin-left: -50vw;
  margin-right: -50vw;
  padding: 2.5rem 0 0;
  background: transparent;
  box-sizing: border-box;

  ${media.laptop`
    padding: 0;
  `}
`;

const JoinInner = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: none;
  margin: 0;

  ${media.laptop`
    flex-direction: row;
    align-items: stretch;
    min-height: 37.6875rem;
  `}
`;

const joinHalfColumn = css`
  ${media.laptop`
    flex: 1 1 0;
    min-width: 0;
  `}
`;

const JoinVisual = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2.5rem 1.25rem;
  min-height: 17.5rem;
  background: linear-gradient(100deg, #fc5200 0%, #ff9b05 100%),
    linear-gradient(0deg, #fd6300 0%, #fd6300 100%);

  ${joinHalfColumn}

  ${media.laptop`
    padding: 3rem;
    min-height: auto;
  `}
`;

const JoinUnicorn = styled.img`
  width: 100%;
  max-width: 24rem;
  height: auto;
`;

const JoinCopy = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 2.5rem 1.25rem 3rem;
  background: #593a27;

  ${joinHalfColumn}

  ${media.laptop`
    padding: 3.75rem 3rem;
  `}
`;

const JoinCopyInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.875rem;
  width: 100%;
  max-width: 36rem;
  margin: 0 auto;

  ${media.laptop`
    gap: 3.125rem;
  `}
`;

const JoinCtaButton = styled(PrimaryLinkButton)`
  ${media.laptop`
    font-size: 1.125rem;
    padding: 1.375rem 1.875rem;
  `}
`;

const SocialSection = styled.section`
  padding: 3rem 1.25rem 4rem;
  text-align: center;

  ${media.laptop`
    padding: 6.25rem 1.25rem;
  `}
`;

const SocialIcons = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 2.5rem;
  margin-top: 3.125rem;

  ${media.laptop`
    margin-top: 4.75rem;
  `}

  a {
    display: block;
    line-height: 0;
  }

  img {
    width: 4rem;
    height: 4rem;
  }
`;

const labeledContactLine = /^(Email|Support|Sales):\s*(.+)$/;

function RegionContactLine({ line }) {
  const { text, href } = line;
  const m = text.match(labeledContactLine);
  if (m) {
    return (
      <RegionLine>
        <RegionLineLabel>{`${m[1]}:`}</RegionLineLabel>
        <RegionValueLink href={href}>{m[2]}</RegionValueLink>
      </RegionLine>
    );
  }
  return (
    <RegionLine>
      <RegionValueLink href={href}>{text}</RegionValueLink>
    </RegionLine>
  );
}

const ContactUsPage = (props) => {
  const { data } = props;
  const { language, i18n } = useI18next();
  const { t } = getLanguageResource('contactUs', data, language);

  const phoneRegionsRaw = t('phoneRegions');
  const phoneRegions = Array.isArray(phoneRegionsRaw) ? phoneRegionsRaw : [];

  const supportHcUrl = `https://support.hostaway.com/hc/${i18n.language.toLowerCase()}`;

  React.useEffect(() => {
    const { referrer, URL: url } = document;
    trackCustomerThroughPages({ referrer, url });
    return setupSmoothScrolling();
  }, []);

  return (
    <Layout {...props}>
      <PageRoot>
        <HeroSection>
          <HeroInner>
            <HeroCopy>
              <Title
                $color="#fff"
                $fontSize="2.25rem"
                $laptopFontSize="3rem"
                $margin="0"
                $lineHeight="130%"
              >
                {t('heroTitle')}
              </Title>
              <Text
                $color="#fff"
                $fontSize="1.125rem"
                $laptopFontSize="1.5rem"
                $lineHeight="150%"
                $margin="0"
              >
                {t('heroSubtitle')}
              </Text>
            </HeroCopy>
            <HeroVisual>
              <HeroCompositeImg src={contactUsHero} alt="" />
            </HeroVisual>
          </HeroInner>
        </HeroSection>

        <Box>
          <CardsSection>
            <CardsGrid>
              <ContactCard>
                <CardIcon src={connectIcon} alt="" />
                <CardSubheading>{t('cardConnectTitle')}</CardSubheading>
                <CardBodyText>{t('cardConnectBody')}</CardBodyText>
                <CardActions>
                  <CardPrimaryButton as={Link} to="/get-free-demo/" $compact>
                    {t('cardConnectPrimary')}
                  </CardPrimaryButton>
                  <ArrowTextLink href="#contact-global-phone-numbers">
                    <img src={arrowRight} alt="" aria-hidden />
                    {t('cardConnectLink')}
                  </ArrowTextLink>
                </CardActions>
              </ContactCard>

              <ContactCard>
                <CardIcon src={supportIcon} alt="" />
                <CardSubheading>{t('cardSupportTitle')}</CardSubheading>
                <CardBodyText>{t('cardSupportBody')}</CardBodyText>
                <CardActions>
                  <CardPrimaryButton
                    href={supportHcUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    $compact
                  >
                    {t('cardSupportPrimary')}
                  </CardPrimaryButton>
                  <ArrowTextLink href="#contact-global-phone-numbers">
                    <img src={arrowRight} alt="" aria-hidden />
                    {t('cardSupportLink')}
                  </ArrowTextLink>
                </CardActions>
              </ContactCard>

              <ContactCard>
                <CardIcon src={partnerIcon} alt="" />
                <CardSubheading>{t('cardPartnerTitle')}</CardSubheading>
                <CardBodyText>{t('cardPartnerBody')}</CardBodyText>
                <CardActions>
                  <CardPrimaryButton
                    href={PARTNERSHIPS_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    $compact
                  >
                    {t('cardPartnerPrimary')}
                  </CardPrimaryButton>
                  <ArrowNavLink to="/referral/">
                    <img src={arrowRight} alt="" aria-hidden />
                    {t('cardPartnerLinkReferral')}
                  </ArrowNavLink>
                  <ArrowNavLink to="/affiliate-program/">
                    <img src={arrowRight} alt="" aria-hidden />
                    {t('cardPartnerLinkAffiliate')}
                  </ArrowNavLink>
                </CardActions>
              </ContactCard>
            </CardsGrid>
          </CardsSection>
        </Box>

        <MapSection>
          <MapInner>
            <Heading
              $textAlign="center"
              $fontSize="1.75rem"
              $laptopFontSize="3rem"
              $margin="0 auto"
              $maxWidth="52.8125rem"
              $color="#2d2926"
              $lineHeight="130%"
            >
              {t('mapTitle')}
            </Heading>
            <Text
              $textAlign="center"
              $margin="2rem auto 0"
              $laptopMargin="3.125rem auto 0"
              $maxWidth="46rem"
              $fontSize="1rem"
              $laptopFontSize="1.125rem"
              $lineHeight="160%"
              $color="#2d2926"
            >
              {t('mapSubtitle')}
            </Text>
            <MapFigure>
              <MapPhoto src={worldMap} alt="" />
              <MapPinsLayer aria-hidden>
                {MAP_PIN_TIP_PCT.map((pos, i) => (
                  <MapPinWrap
                    key={`map-pin-${i}`}
                    $left={pos.left}
                    $top={pos.top}
                  >
                    <MapPinPing
                      $duration={MAP_PIN_PING_DURATION_SEC[i]}
                      $delay={MAP_PIN_PING_DELAY_SEC[i]}
                    />
                    <MapPinImg
                      src={mapPin}
                      alt=""
                      $duration={MAP_PIN_PING_DURATION_SEC[i]}
                      $delay={MAP_PIN_PING_DELAY_SEC[i]}
                    />
                  </MapPinWrap>
                ))}
              </MapPinsLayer>
            </MapFigure>

            {phoneRegions.length > 0 && (
              <RegionalPhonesWrap id="contact-global-phone-numbers">
                <RegionalPhonesGrid>
                  {phoneRegions.map((region) => {
                    const flagSrc = REGION_FLAG_SRC[region.flagCode];
                    return (
                      <PhoneRegionBlock key={region.title}>
                        <RegionTitleRow>
                          <RegionTitle>{region.title}</RegionTitle>
                          {flagSrc && (
                            <FlagImg src={flagSrc} alt="" aria-hidden />
                          )}
                        </RegionTitleRow>
                        <RegionLines>
                          {region.lines.map((line, idx) => (
                            <RegionContactLine
                              key={`${region.title}-${idx}`}
                              line={line}
                            />
                          ))}
                        </RegionLines>
                      </PhoneRegionBlock>
                    );
                  })}
                </RegionalPhonesGrid>
              </RegionalPhonesWrap>
            )}
          </MapInner>
        </MapSection>

        <CTASectionWrapper>
          <CTASection
            ctaHeading={t('bottomCtaHeading')}
            fontSize="1.375rem"
            laptopFontSize="2.375rem"
            color="sienna"
            primaryButtonText={t('getStarted')}
            primaryButtonLink="/get-free-demo/"
            secondaryButtonText={t('getPricing')}
            secondaryButtonLink="/pricing/"
          />
        </CTASectionWrapper>

        <JoinSection>
          <JoinInner>
            <JoinVisual>
              <JoinUnicorn src={unicornJoinTeam} alt="" />
            </JoinVisual>
            <JoinCopy>
              <JoinCopyInner>
                <Heading
                  $fontSize="2.25rem"
                  $laptopFontSize="3rem"
                  $margin="0"
                  $color="#faf7f2"
                  $lineHeight="130%"
                >
                  {t('joinTitle')}
                </Heading>
                <Text
                  $margin="0"
                  $fontSize="1rem"
                  $laptopFontSize="1.5rem"
                  $lineHeight="150%"
                  $color="#faf7f2"
                >
                  {t('joinBody')}
                </Text>
                <JoinCtaButton
                  href="https://hostaway.recruitee.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  $alignSelf="flex-start"
                  $compact
                >
                  {t('joinCta')}
                </JoinCtaButton>
              </JoinCopyInner>
            </JoinCopy>
          </JoinInner>
        </JoinSection>

        <SocialSection>
          <Heading
            $textAlign="center"
            $fontSize="2.25rem"
            $laptopFontSize="3rem"
            $margin="0 auto"
            $maxWidth="52.8125rem"
            $color="#2d2926"
          >
            {t('socialTitle')}
          </Heading>
          <Text
            $textAlign="center"
            $margin="1.875rem auto 0"
            $laptopMargin="3.125rem auto 0"
            $fontSize="1rem"
            $laptopFontSize="1.5rem"
            $color="#2d2926"
          >
            {t('socialSubtitle')}
          </Text>
          <SocialIcons>
            <a href="https://www.facebook.com/Hostawaycom" target="_blank" rel="noreferrer noopener">
              <img src={facebook} alt="Facebook" />
            </a>
            <a href="https://x.com/hostaway" target="_blank" rel="noreferrer noopener">
              <img src={twitter} alt="X" />
            </a>
            <a href="https://www.instagram.com/hostawaycom" target="_blank" rel="noreferrer noopener">
              <img src={instagram} alt="Instagram" />
            </a>
            <a href="https://www.linkedin.com/company/hostaway" target="_blank" rel="noreferrer noopener">
              <img src={linkedin} alt="LinkedIn" />
            </a>
            <a
              href="https://www.youtube.com/channel/UC9QeuvQX1EurqelmrXkgaGw"
              target="_blank"
              rel="noreferrer noopener"
            >
              <img src={youtube} alt="YouTube" />
            </a>
          </SocialIcons>
        </SocialSection>
      </PageRoot>
    </Layout>
  );
};

export default ContactUsPage;

export const Head = ({ pageContext: { language, i18n } }) => (
  <SEO language={language} originalPath={i18n.originalPath}>
    <title>
      {META_TITLES.contactUs[language]} - {BASE_TITLES[language]}
    </title>
    <meta
      name="description"
      content={META_DESCRIPTIONS.contactUs[language] || ''}
    />
  </SEO>
);
